import { useState } from "react";
import { useIntl } from "react-intl";
import { AlertTriangle, Eye, EyeOff, Mail } from "react-feather";

export function GroupWrapper({ children, className = "" }) {
  return (
    <div className={`relative max-w-[360px] ${className}`}>{children}</div>
  );
}

export function Label({ children, error, className = "", ...props }) {
  return (
    <label
      className={`m-0 block w-fit -translate-y-[84px] p-0 text-left text-[12px] leading-[15px] font-normal transition-transform duration-300 ${
        error ? "invisible -translate-y-10 opacity-0" : ""
      } ${className}`}
      {...props}
    >
      {children}
    </label>
  );
}

export function Input({ error, className = "", ...props }) {
  return (
    <input
      className={`w-full rounded-[2px] text-base/[20px] font-normal mb-[23px] bg-clip-padding px-[12px] py-[10px] transition-[border-color,box-shadow] duration-150 ease-in-out ${
        error ? "border border-[#ff4444]" : "border-primary-black border"
      } [&::-ms-clear]:hidden [&::-ms-clear]:h-0 [&::-ms-clear]:w-0 [&::-ms-reveal]:hidden [&::-ms-reveal]:h-0 [&::-ms-reveal]:w-0 [&::placeholder]:text-[14px] [&::placeholder]:text-black/40 [&:placeholder-shown+label]:invisible [&:placeholder-shown+label]:-translate-y-10 [&:placeholder-shown+label]:opacity-0 [&:user-invalid]:border-red-500 [&:user-invalid]:outline-red-500 ${className}`}
      {...props}
    />
  );
}

export function InputErrorMessage({ children, className = "" }) {
  return (
    <div
      className={`relative -top-[7px] text-[12px] leading-[15px] font-normal text-[#ff4444] ${className}`}
    >
      {children}
    </div>
  );
}

export function IconWrapper({ children, className = "", ...props }) {
  return (
    <div className={`absolute top-[12px] right-[12px] ${className}`} {...props}>
      {children}
    </div>
  );
}

function InputWrapper({ children, className = "" }) {
  return <div className={`relative ${className}`}>{children}</div>;
}


const InputModule = ({
  type,
  identifier,
  autoComplete,
  placeholder,
  value,
  onChange,
  onBlur,
  required,
  onInvalid,
  error,
  maxLength,
  minLength,
  pattern,
  "aria-label": ariaLabel,
}) => {
  const intl = useIntl();
  const [showPassword, setShowPassword] = useState(false);

  // Descriptor objects ({ id, defaultMessage }) resolve against the locale
  // bundle; plain strings render as-is.
  const errorMessage =
    error && typeof error === "object"
      ? error.id
        ? intl.formatMessage({
            id: error.id,
            defaultMessage: error.defaultMessage,
          })
        : error.defaultMessage
      : error;

  const handleChangePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <GroupWrapper>
      {error && (
        <InputErrorMessage className="form__error text-left">
          {errorMessage}
        </InputErrorMessage>
      )}
      <InputWrapper>
        <Input
          type={
            (identifier === "password" || identifier === "confirmPassword") &&
            showPassword
              ? "text"
              : type
          }
          id={identifier}
          name={identifier}
          autoComplete={autoComplete}
          placeholder={`*${placeholder}`}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          onInvalid={onInvalid}
          error={error}
          minLength={minLength}
          maxLength={maxLength}
          pattern={pattern}
          aria-label={ariaLabel}
        />
        <Label htmlFor={identifier} error={error}>
          {placeholder}
        </Label>
        <IconWrapper
          style={{
            cursor:
              identifier === "password" || identifier === "confirmPassword"
                ? "pointer"
                : "auto",
          }}
          onClick={
            identifier === "password" || identifier === "confirmPassword"
              ? handleChangePasswordVisibility
              : undefined
          }
        >
          {error ? (
            <AlertTriangle color={"red"} size={18} />
          ) : identifier === "password" || identifier === "confirmPassword" ? (
            showPassword ? (
              <EyeOff size={16} />
            ) : (
              <Eye size={16} />
            )
          ) : identifier === "email" ? (
            <Mail size={16} />
          ) : null}
        </IconWrapper>
      </InputWrapper>
    </GroupWrapper>
  );
};

export default InputModule;

